import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { axios, apiRequestOptions } from 'utils';
import { apiPath } from 'constants/apiPathConstants';
import AuditAuthor from 'components/AuditAuthor';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    filterField: {
        minWidth: 200,
    },
    loadingWrapper: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    empty: {
        padding: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    cell: {
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        padding: '6px 12px',
    },
    cellWrap: {
        fontSize: '0.8rem',
        padding: '6px 12px',
        maxWidth: 160,
        wordBreak: 'break-word',
    },
    entryType: {
        display: 'inline-block',
        marginRight: 6,
        padding: '1px 6px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        verticalAlign: 'middle',
    },
    entryType_field_change: { background: '#e2e8f0', color: '#334155' },
    entryType_business_event: { background: '#dbeafe', color: '#1e40af' },
    entryType_security_event: { background: '#fee2e2', color: '#991b1b' },
    oldValue: {
        color: '#c62828',
        textDecoration: 'line-through',
    },
    newValue: {
        color: '#2e7d32',
    },
}));

const EntityChangeHistoryDialog = ({ open, entityType, entityId, title, onClose }) => {
    const classes = useStyles();
    const [t] = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterField, setFilterField] = useState('');

    useEffect(() => {
        if (!open || !entityType || !entityId) {
            setLogs([]);
            setFilterField('');
            return;
        }
        setLoading(true);
        setFilterField('');
        axios(apiRequestOptions(apiPath.ENTITY_CHANGE_LOGS.LIST, {
            entityType,
            entityId,
            'order[changedAt]': 'desc',
            itemsPerPage: 500,
        }))
            .then(res => setLogs(res.data || []))
            .catch(() => setLogs([]))
            .finally(() => setLoading(false));
    }, [open, entityType, entityId]);

    const uniqueFields = useMemo(() => {
        const fields = [...new Set(logs.map(l => l.fieldName))];
        return fields.sort();
    }, [logs]);

    const visibleLogs = useMemo(() => {
        if (!filterField) return logs;
        return logs.filter(l => l.fieldName === filterField);
    }, [logs, filterField]);

    const formatDateTime = val => {
        if (!val) return '—';
        return new Date(val).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });
    };

    const getFieldLabel = field => {
        const key = `page.competitionSetting.users.profile.changeHistory.fields.${field}`;
        const translated = t(key);
        return translated === key ? field : translated;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {title || t('page.competitionSetting.users.profile.changeHistory.title')}
                <IconButton className={classes.closeButton} onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {!loading && logs.length > 0 && (
                    <div className={classes.toolbar}>
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            className={classes.filterField}
                            label={t('page.competitionSetting.users.profile.changeHistory.filterByField')}
                            value={filterField}
                            onChange={e => setFilterField(e.target.value)}
                        >
                            <MenuItem value="">
                                {t('page.competitionSetting.users.profile.changeHistory.allFields')}
                            </MenuItem>
                            {uniqueFields.map(field => (
                                <MenuItem key={field} value={field}>
                                    {getFieldLabel(field)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                )}

                {loading && (
                    <div className={classes.loadingWrapper}>
                        <CircularProgress />
                    </div>
                )}

                {!loading && visibleLogs.length === 0 && (
                    <Typography className={classes.empty}>
                        {t('page.competitionSetting.users.profile.changeHistory.noLogs')}
                    </Typography>
                )}

                {!loading && visibleLogs.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.cell}>
                                    {t('page.competitionSetting.users.profile.changeHistory.table.date')}
                                </TableCell>
                                <TableCell className={classes.cell}>
                                    {t('page.competitionSetting.users.profile.changeHistory.table.changedBy')}
                                </TableCell>
                                <TableCell className={classes.cell}>
                                    {t('page.competitionSetting.users.profile.changeHistory.table.field')}
                                </TableCell>
                                <TableCell className={classes.cell}>
                                    {t('page.competitionSetting.users.profile.changeHistory.table.oldValue')}
                                </TableCell>
                                <TableCell className={classes.cell}>
                                    {t('page.competitionSetting.users.profile.changeHistory.table.newValue')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleLogs.map(log => (
                                <TableRow key={log.id} hover>
                                    <TableCell className={classes.cell}>
                                        {formatDateTime(log.changedAt)}
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        {/* Backend resolves "FirstName LastName" (profile) → username. */}
                                        <AuditAuthor log={log} />
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        {/* One timeline for all three kinds: the badge is what
                                            distinguishes a field change from a business or
                                            security event, so no separate viewer is needed. */}
                                        <span
                                            className={clsx(
                                                classes.entryType,
                                                classes[`entryType_${log.entryType || 'field_change'}`],
                                            )}
                                            data-testid="audit-entry-type"
                                            data-entry-type={log.entryType || 'field_change'}
                                        >
                                            {t(
                                                `audit.entryType.${log.entryType || 'field_change'}`,
                                                log.entryType || 'field_change',
                                            )}
                                        </span>
                                        {log.action
                                            ? t(`audit.action.${log.action}`, log.action)
                                            : getFieldLabel(log.fieldName)}
                                    </TableCell>
                                    <TableCell className={classes.cellWrap}>
                                        <span className={classes.oldValue}>
                                            {log.oldValue ?? '—'}
                                        </span>
                                    </TableCell>
                                    <TableCell className={classes.cellWrap}>
                                        <span className={classes.newValue}>
                                            {log.newValue ?? '—'}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
        </Dialog>
    );
};

EntityChangeHistoryDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    entityType: PropTypes.string,
    entityId: PropTypes.string,
    title: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

EntityChangeHistoryDialog.defaultProps = {
    entityType: null,
    entityId: null,
    title: null,
};

export default EntityChangeHistoryDialog;
